import { useState } from "react"
import { Check, Star } from "lucide-react"
import { Container } from "@/components/layout/Container"
import { Eyebrow } from "@/components/shared/Eyebrow"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import BuyNowButton from "@/features/checkout/BuyNowButton"
import AddToCartButton from "@/features/checkout/AddToCartButton"
import type { TreatmentProps, TreatmentWeeks } from "@/content/treatments"
import { treatmentCatalogId } from "@/content/treatments"
import { getTreatmentItem, getItemName, getDisplayPrice, formatMXN, promotionFromActive } from "@/content/prices"
import { cn } from "@/lib/utils"
import { useActivePromotionForItem } from "@/features/promotions/ActivePromotionProvider"

interface PlanMeta {
	duration: TreatmentWeeks
	name: string
	length: string
	blurb: string
	recommended: boolean
}

const PLANS: PlanMeta[] = [
	{ duration: "two", name: "Paquete Básico", length: "2 semanas", blurb: "Para empezar o casos leves", recommended: false },
	{ duration: "four", name: "Paquete Completo", length: "1 mes", blurb: "Resultados más profundos", recommended: true }
]

/** Precio final con precio de lista tachado y % de descuento cuando hay promo. */
function PriceTag({ price, itemId }: { price: number; itemId: string }): React.JSX.Element {
	const promo = promotionFromActive(useActivePromotionForItem(itemId))
	const { original, final, hasDiscount, discountLabel } = getDisplayPrice(price, promo, itemId)
	return (
		<div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
			<span className="text-3xl font-bold text-primary">{formatMXN(final)}</span>
			{hasDiscount && (
				<>
					<span className="text-base text-foreground line-through">{formatMXN(original)}</span>
					<Badge variant="destructive">-{discountLabel}</Badge>
				</>
			)}
		</div>
	)
}

interface TreatmentPlansProps {
	treatment: TreatmentProps
	tagline: string
}

/**
 * Sección 1 (encabezado + detalles): tarjetas comparativas Básico (2 sem.) y
 * Completo (1 mes). En móvil se muestra UNA sola tarjeta con un toggle encima
 * (Completo activado por defecto) y transición de opacidad al cambiar; ambas
 * tarjetas comparten la misma celda de grid, así la altura se reserva a la más
 * alta y el contenido de la página no salta. Desde `md` se ven las dos tarjetas.
 */
export function TreatmentPlans({ treatment, tagline }: TreatmentPlansProps): React.JSX.Element {
	const [showComplete, setShowComplete] = useState(true)

	return (
		<section className="relative overflow-hidden bg-background bg-vignette">
			<Container className="relative py-14 md:py-20">
				<div className="mx-auto max-w-2xl text-center">
					<Eyebrow align="center">Bioestimulación celular</Eyebrow>
					<h1 className="mt-3 text-balance font-heading text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
						Tratamiento para {treatment.as} {treatment.name.toLowerCase()}
					</h1>
					<p className="mx-auto mt-4 max-w-xl text-pretty text-lg text-muted-foreground">{tagline}</p>
				</div>

				{/* Toggle solo en móvil: elige qué tarjeta se muestra. */}
				<div className="mt-10 flex items-center justify-center gap-3 md:hidden">
					<Switch id="plan-toggle" checked={showComplete} onCheckedChange={setShowComplete} />
					<Label htmlFor="plan-toggle" className="cursor-pointer text-base">
						{showComplete ? "Paquete completo" : "Paquete básico"}
					</Label>
				</div>

				<div className="mx-auto mt-6 grid max-w-4xl grid-cols-1 items-stretch gap-6 md:mt-12 md:grid-cols-2">
					{PLANS.map(plan => {
						const item = getTreatmentItem(treatment.id, plan.duration)
						const products = treatment.duration[plan.duration].products
						const cartItem = { id: treatment.id, priceId: item.priceId, amount: 1, duration: plan.duration }
						// En móvil ambas tarjetas ocupan la misma celda (crossfade sin salto);
						// desde `md` cada una vuelve a su columna y siempre son visibles.
						const isActive = plan.recommended ? showComplete : !showComplete
						return (
							<Card
								key={plan.duration}
								aria-hidden={!isActive || undefined}
								className={cn(
									"relative flex flex-col transition-opacity duration-300 [grid-area:1/1] md:pointer-events-auto md:[grid-area:auto] md:opacity-100",
									isActive ? "opacity-100" : "pointer-events-none opacity-0",
									plan.recommended && "ring-2 ring-primary md:-translate-y-2 md:shadow-xl"
								)}
							>
								<img
									src={item.image.source}
									alt={item.image.alt}
									className="aspect-4/3 w-full object-cover"
									loading="lazy"
								/>
								<CardHeader>
									<div className="flex items-start justify-between gap-3">
										<div className="space-y-2">
											<CardTitle className="text-lg">{plan.name}</CardTitle>
											<Badge variant="secondary">Duración: {plan.length}</Badge>
										</div>
										{plan.recommended && (
											<Badge className="gap-1">
												<Star className="fill-current" />
												Recomendado
											</Badge>
										)}
									</div>
									<p className="mt-2 text-sm text-muted-foreground">{plan.blurb}</p>
								</CardHeader>
								<CardContent className="flex flex-1 flex-col gap-5">
									<PriceTag price={item.price} itemId={treatmentCatalogId(treatment.id, plan.duration)} />
									<div>
										<p className="text-sm font-medium">Incluye:</p>
										<ul className="mt-2 space-y-1.5">
											{products.map(p => (
												<li key={p.id} className="flex items-start gap-2 text-sm text-muted-foreground">
													<Check className="mt-0.5 size-4 shrink-0 text-primary" />
													<span>
														{p.amount}× {getItemName(p.id)}
													</span>
												</li>
											))}
										</ul>
									</div>
									<div className="mt-auto flex flex-col gap-2 pt-2">
										<BuyNowButton
											cartItem={cartItem}
											size="lg"
											variant={plan.recommended ? "default" : "secondary"}
											className="w-full"
										/>
										<AddToCartButton cartItem={cartItem} size="lg" className="w-full" />
									</div>
								</CardContent>
							</Card>
						)
					})}
				</div>
			</Container>
		</section>
	)
}
